import pluralize from "pluralize";
import { z } from "zod";
import { assertUnreachable } from "../helpers/utilities.js";
import {
  automationDeliveryProviderSchema,
  automationReportSourceSchema,
  storedAutomationReportConfigSchema
} from "./automation.js";
const draftRecipientSelectionSchema = z.object({
  teammate_ids: z.array(z.string()).describe("Directory ids of individually named people."),
  group_ids: z.array(z.string()).describe(
    "Directory ids of named groups \u2014 naming a group reaches every member."
  )
});
const draftRecipientsDestinationSchema = z.object({
  type: z.literal("recipients"),
  primary: draftRecipientSelectionSchema.describe(
    "The people the instructions say should receive these reports. Leave empty rather than inventing an audience."
  ),
  subscribers: draftRecipientSelectionSchema.describe(
    "People copied on every report in addition to its primary recipients."
  )
});
const draftChannelsDestinationSchema = z.object({
  type: z.literal("channels"),
  channels: z.array(z.string()).describe(
    "Chat channel names exactly as the instructions write them. Channels never deliver over email."
  )
});
const draftInstanceRecipientRoutingSchema = z.object({
  type: z.literal("instance_recipient"),
  subscribers: draftRecipientSelectionSchema.describe(
    "People copied on every report in addition to the person it is about \u2014 the same set on each one, so a per-subject audience cannot be expressed here."
  )
});
const draftMappedEntryDestinationSchema = z.union([
  draftRecipientsDestinationSchema.omit({ subscribers: true }),
  draftChannelsDestinationSchema
]);
const draftMappedRoutingSchema = z.object({
  type: z.literal("mapped"),
  entries: z.array(
    z.object({
      instance: z.union([
        z.object({
          type: z.literal("manager"),
          manager_id: z.string().describe("Directory id of the manager this entry routes.")
        }),
        z.object({
          type: z.literal("group"),
          group_id: z.string().describe("Directory id of the group this entry routes.")
        })
      ]),
      destination: draftMappedEntryDestinationSchema
    })
  ).describe(
    `One destination per manager or group, for instructions like "post each team's report in its own channel". Entries name the same kind as the definition's instances. An instance without an entry gets no delivery.`
  )
});
const draftTeammateSelectionSchema = z.union([
  z.object({ type: z.literal("all") }),
  z.object({
    type: z.literal("specific"),
    teammate_ids: z.array(z.string()).describe("Directory ids of individually named people."),
    group_ids: z.array(z.string()).describe("Directory ids of named groups whose members are selected.")
  })
]);
const draftReportInstancesSchema = z.union([
  z.object({
    type: z.literal("shared"),
    selection: draftTeammateSelectionSchema.describe(
      "Whose calls the single report covers. Use all only when the instructions do not restrict it to named people or groups."
    )
  }).describe(
    "A single report read alike by whoever receives it. For digests and combined summaries; analyzing several people in ONE report is still this kind."
  ),
  z.object({
    type: z.literal("teammate"),
    selection: draftTeammateSelectionSchema.describe(
      "The teammates who each get their own report. A selected group's members who manage nobody each get one."
    )
  }).describe("One report per teammate about their own calls."),
  z.object({
    type: z.literal("manager"),
    selection: draftTeammateSelectionSchema.describe(
      "The managers who each get a report. A selected group selects its members who manage a group. Someone who manages no group gets no report."
    )
  }).describe("One report per manager about their team."),
  z.object({
    type: z.literal("group"),
    selection: z.union([
      z.object({ type: z.literal("all") }),
      z.object({
        type: z.literal("specific"),
        group_ids: z.array(z.string())
      })
    ])
  }).describe("One report per group about its members.")
]);
const draftReportDefinitionSchema = z.object({
  instances: draftReportInstancesSchema,
  source: automationReportSourceSchema.describe(
    "calls: each report analyzes its subjects' calls. rollup: each report is a rollup written from the run's other reports about its subjects \u2014 a manager's rollup reads the reports about the members of their team."
  ),
  routing: z.union([
    draftRecipientsDestinationSchema,
    draftChannelsDestinationSchema,
    draftInstanceRecipientRoutingSchema,
    draftMappedRoutingSchema
  ]).describe("Where each report of this definition goes.")
});
const automationInterpretationDraftSchema = z.object({
  provider: automationDeliveryProviderSchema.describe(
    "The requested delivery provider. Use email when none is specified. Preserve an explicitly requested disconnected provider so compilation can apply and explain the email fallback."
  ),
  definitions: z.array(draftReportDefinitionSchema).describe(
    "The report definitions the agent produces each run: one per instance kind and source, so reports of one kind for several named people are one definition naming them all."
  ),
  unresolved_requests: z.array(z.string()).describe(
    "One entry per instruction about structure or delivery that could not be expressed in the fields above \u2014 an unknown name, an unsupported shape, a disconnected provider. Each is reported to the agent's editors. Say what was asked and why it could not be honored."
  )
});
const resolvedAutomationRunInterpretationSchema = z.object({
  status: z.literal("resolved"),
  report_definition: storedAutomationReportConfigSchema,
  transport: z.object({ provider: automationDeliveryProviderSchema }),
  warnings: z.array(z.string())
});
const automationRunInterpretationSchema = z.discriminatedUnion(
  "status",
  [
    resolvedAutomationRunInterpretationSchema,
    z.object({
      status: z.literal("unresolved"),
      error: z.string()
    })
  ]
);
function formatNameList(names, maxNames = 5) {
  const shown = names.slice(0, maxNames);
  const overflow = names.length - shown.length;
  return overflow > 0 ? `${shown.join(", ")}, +${overflow} more` : shown.join(", ");
}
function listSelectedNames(instances, names) {
  const groupNames = (ids) => ids.map((id) => names.groupNamesById.get(id) ?? "a group");
  switch (instances.type) {
    case "shared":
    case "teammate":
    case "manager": {
      const { selection } = instances;
      switch (selection.type) {
        case "all":
          return null;
        case "specific":
          return [
            ...selection.teammate_ids.map(
              (id) => names.teammateNamesById.get(id) ?? "a teammate"
            ),
            ...groupNames(selection.group_ids)
          ];
        default:
          return assertUnreachable(selection);
      }
    }
    case "group": {
      const { selection } = instances;
      switch (selection.type) {
        case "all":
          return null;
        case "specific":
          return groupNames(selection.group_ids);
        default:
          return assertUnreachable(selection);
      }
    }
    default:
      return assertUnreachable(instances);
  }
}
function describeSource(source) {
  switch (source) {
    case "calls":
      return { result: "result", builtFrom: "" };
    case "rollup":
      return {
        result: "summary",
        builtFrom: " built from the run's other results"
      };
    default:
      return assertUnreachable(source);
  }
}
function describeReportInstances(definition, names) {
  const selectedNames = listSelectedNames(definition.instances, names);
  if (selectedNames?.length === 0) {
    return "no result \u2014 none of the people or groups it named were found";
  }
  const selectedList = selectedNames ? ` (${formatNameList(selectedNames)})` : "";
  const { result, builtFrom } = describeSource(definition.source);
  const { instances } = definition;
  if (instances.type === "shared") {
    return `one shared ${result}${selectedNames ? ` about selected people${selectedList}` : ""}${builtFrom}`;
  }
  const noun = instances.type;
  return selectedNames ? `one ${result} per selected ${noun}${selectedList}${builtFrom}` : `one ${result} per ${noun}${builtFrom}`;
}
function describeReportRouting(routing, provider) {
  switch (routing.type) {
    case "instance_recipient": {
      const subscriberCount = routing.subscribers.teammate_ids.length;
      return subscriberCount > 0 ? `delivered privately by ${provider}, copying ${pluralize("subscriber", subscriberCount, true)}` : `delivered privately by ${provider} to the person it is about`;
    }
    case "recipients": {
      const recipientCount = routing.primary.teammate_ids.length + routing.primary.group_ids.length;
      return recipientCount > 0 ? `sent by ${provider} to ${pluralize("recipient", recipientCount, true)}` : "sent to no one \u2014 the instructions name no recipients";
    }
    case "channels":
      return routing.channels.length > 0 ? `posted to ${routing.channels.map((channel) => channel.name ?? channel.id).join(", ")}` : "posted to no channel \u2014 none of the named channels were found";
    case "mapped":
      return `sent by ${provider} to ${pluralize("mapped destination", routing.entries.length, true)}`;
    default:
      return assertUnreachable(routing);
  }
}
function describeAutomationReportConfig(reportConfig, provider, names) {
  const clauses = reportConfig.definitions.map(
    (definition) => `${describeReportInstances(definition, names)}, ${describeReportRouting(definition.routing, provider)}`
  );
  const sentence = clauses.join("; plus ");
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}
export {
  automationInterpretationDraftSchema,
  automationRunInterpretationSchema,
  describeAutomationReportConfig,
  resolvedAutomationRunInterpretationSchema
};
//# sourceMappingURL=automationInterpretation.js.map
